import type React from "react";
import { createContext, useContext, useEffect, useRef, useState } from "react";
import { useTheme } from "@mui/material";
import { useForm, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { HTTPClient } from "../../HTTPClient";
import type { CampaignContextType } from "../../types/campaign/CampaignContext";
import { campaignFormSchema, campaignSaveSchema } from "../../schemas/campaign";
import type { CampaignFormValues, Gift } from "../../schemas/campaign";
import { useSnackbar } from "../../providers/ToastProvider";
import { useTranslation } from "react-i18next";
import type { Action, Campaign, CampaignConfiguration } from "@tgc/types";
import { isActionValid } from "../../utils/actions";
import { usePlace } from "../../providers/PlaceProvider";

const CampaignContext = createContext<CampaignContextType | undefined>(undefined);

const CampaignProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t } = useTranslation("", { keyPrefix: "views.campaign.save" });
  const { palette } = useTheme();
  const { snackbarShowMessage } = useSnackbar();
  const { selectedPlace } = usePlace();

  const form = useForm<CampaignFormValues>({
    resolver: zodResolver(campaignFormSchema),
    defaultValues: {
      label: "",
      game: "WHEEL",
      primaryColor: palette.primary.main,
      secondaryColor: palette.secondary.main,
      logo: "",
      logoUri: "",
      startDate: undefined,
      isDisabled: false,
      profile: "PREMIUM",
      theme: "DEFAULT",
      kioskMode: false,
      skipCooldown: false,
      controlledReviews: false,
      voucherStart: 1,
      voucherExpiration: null,
      promoCodes: [],
    },
  });

  const { setValue, getValues, watch, reset } = form;
  const formValues = watch();

  const [id, setID] = useState<string>(selectedPlace?.id || "");
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const [campaign, setCampaign] = useState<CampaignConfiguration | null>(null);
  const [gifts, setGifts] = useState<Gift[]>([]);
  // last place whose campaign we asked for; guards fetchCampaign against out-of-order responses
  const requestedPlaceRef = useRef<string | null>(null);
  const giftsRef = useRef<Gift[]>([]);
  useEffect(() => { giftsRef.current = gifts; }, [gifts]);
  const defaultGetGifts = () => giftsRef.current;
  const getGiftsForSaveRef = useRef<() => Gift[]>(defaultGetGifts);
  // returns the unregister: a getter left behind by an unmounted GiftTable kept sending that
  // table's gifts on every later save, whatever place was selected by then
  const registerGetGiftsForSave = (fn: () => Gift[]) => {
    getGiftsForSaveRef.current = fn;
    return () => {
      if (getGiftsForSaveRef.current === fn) getGiftsForSaveRef.current = defaultGetGifts;
    };
  };
  const [actions, setActions] = useState<Action[]>([]);
  const [placeId, setPlaceId] = useState<string>("");
  const [wizardMode, setWizardMode] = useState<boolean>(false);

  const createCampaignConfigurationFromData = (data: Campaign) => {
    const { configuration } = data;
    setID(data.id || "");
    setCampaign(configuration);
    setGifts(configuration.gifts ?? []);
    // ponytail: config actions are CampaignAction (no DB columns); widen CampaignContextType.actions to CampaignAction[] if consumers ever need the real row type
    setActions((configuration.actions ?? []) as Action[]);
    setPlaceId(data.placeId ?? "");
    setWizardMode(!configuration.actions?.length && !configuration.gifts?.length);
    reset({
      label: data.label ?? "",
      game: (configuration.game_type ?? "WHEEL") as CampaignFormValues["game"],
      primaryColor: configuration.colors?.primary ?? palette.primary.main,
      secondaryColor: configuration.colors?.secondary ?? palette.secondary.main,
      logo: "",
      logoUri: configuration.logo_uri ?? "",
      startDate: configuration.start_date,
      isDisabled: configuration.disabled ?? false,
      profile: (configuration.profile ?? "PREMIUM") as CampaignFormValues["profile"],
      theme: configuration.theme ?? "DEFAULT",
      kioskMode: configuration.kiosk_mode ?? false,
      skipCooldown: (data as any).skipCooldown ?? (configuration as any).skipCooldown ?? false,
      controlledReviews: (configuration as any).controlledReviews ?? false,
      voucherStart: (data as any).voucherStart ?? (configuration as any).voucherStart ?? 1,
      voucherExpiration: (data as any).voucherExpiration ?? (configuration as any).voucherExpiration ?? null,
      promoCodes: (data as any).promoCodes ?? [],
    });
  };

  const saveCampaign = ({ wizardStep, forceSave }: { wizardStep?: number; forceSave?: boolean } = {}) => {
    return new Promise<Campaign | undefined>((resolve, reject) => {
      setIsSaving(true);

      if (!id) {
        snackbarShowMessage(t("errors.must-add-place"), "error");
        setIsSaving(false);
        return reject();
      }

      // `id` and the form both come from the last campaign response; until the selected place's
      // campaign has loaded they still belong to the previous enseigne, and saving would write one
      // shop's gifts onto the other
      if (id !== selectedPlace?.id) {
        snackbarShowMessage(t("errors.place-not-loaded"), "error");
        setIsSaving(false);
        return reject();
      }

      const currentGifts = getGiftsForSaveRef.current();
      const values = getValues();

      if (!forceSave) {
        if (actions.some(a => !isActionValid(a))) {
          snackbarShowMessage(t("errors.actions.target_missing"), "error");
          setIsSaving(false);
          return reject();
        }

        const steps = ["actions", "game", "customization", "gifts", "conditions"];
        const shouldCheck = (step: string) => wizardStep !== undefined ? wizardStep >= steps.indexOf(step) : true;

        const result = campaignSaveSchema.safeParse({
          ...values,
          gifts: currentGifts,
          checkGifts: shouldCheck("gifts"),
          actions,
          isProxy: campaign?.is_proxy ?? false,
        });

        if (!result.success) {
          // Zod reports issues in key-declaration order; surface the actionable ones first
          const priority = ["actions", "gifts", "promoCodes"];
          const rank = (i: (typeof result.error.issues)[number]) => {
            const index = priority.indexOf(String(i.path[0]));
            return index === -1 ? priority.length : index;
          };
          const issue = [...result.error.issues].sort((a, b) => rank(a) - rank(b))[0];
          // built-in Zod messages aren't i18n keys -> generic translated fallback
          const key = issue.message.startsWith("errors.") ? issue.message : "errors.invalid";
          snackbarShowMessage(t(key), "error");
          setIsSaving(false);
          return reject();
        }
      }

      const campaignData = {
        id,
        gifts: currentGifts.map((gift) => ({
          ...gift,
          ...(gift.id && !gift.id.startsWith("new") && { id: gift.id }),
          initial_limit: gift.initial_limit ?? gift.limit,
        })),
        actions: actions.map(({ ...action }) => ({ ...action })),
        game_type: values.game,
        primaryColor: values.primaryColor,
        secondaryColor: values.secondaryColor,
        logo_uri: values.logoUri,
        label: values.label,
        start_date: values.startDate,
        disabled: values.isDisabled,
        profile: values.profile,
        theme: values.theme,
        kiosk_mode: values.kioskMode,
        is_proxy: campaign?.is_proxy ?? false,
        targets: campaign?.targets ?? [],
        skipCooldown: values.skipCooldown,
        controlledReviews: values.controlledReviews,
        voucherStart: values.voucherStart ?? 1,
        voucherExpiration: values.voucherExpiration ?? null,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        promoCodes: values.promoCodes, // full list = source of truth; backend deletes codes absent from this array
      };

      HTTPClient.post(`/campaign/`, campaignData)
        .then(({ data }) => {
          resolve(data);
          snackbarShowMessage(t("success"));
        })
        .catch((err) => {
          if (err.response?.status === 413) {
            snackbarShowMessage(t("errors.content_too_large"), "error");
            return;
          }
          if (err.response?.status === 403) {
            snackbarShowMessage(t("errors.forbidden"), "error");
            return;
          }
          reject();
          snackbarShowMessage(t("unknown-error"), "error");
        })
        .finally(() => {
          setIsSaving(false);
        });
    });
  };

  const fetchCampaign = () => {
    if (!selectedPlace) return;
    const placeId = selectedPlace.id;
    // switching places fires one fetch per selection, and responses can land out of order.
    // without this guard a slower response for a place that is no longer selected wins,
    // so the form shows the previous enseigne's gifts *and* saves to its campaign id.
    requestedPlaceRef.current = placeId;
    setIsLoaded(false);
    console.log("Fetching campaign: " + placeId);
    HTTPClient.get("/campaign/" + placeId)
      .then(({ data }: { data: Campaign }) => {
        if (requestedPlaceRef.current !== placeId) return;
        createCampaignConfigurationFromData(data);
        setIsLoaded(true);
      })
      .catch(() => {
        if (requestedPlaceRef.current === placeId) setIsLoaded(true);
      });
  };

  useEffect(() => {
    console.log("Triggered fetchCampaign");
    fetchCampaign();
  }, [selectedPlace?.id]);

  const contextValue: CampaignContextType = {
    id,
    campaign,
    gifts,
    actions,
    game: formValues.game,
    primaryColor: formValues.primaryColor,
    secondaryColor: formValues.secondaryColor,
    logo: formValues.logo,
    logoUri: formValues.logoUri,
    startDate: formValues.startDate,
    label: formValues.label,
    placeId,
    isLoaded,
    isSaving,
    isDisabled: formValues.isDisabled,
    profile: formValues.profile,
    wizardMode,
    setID,
    setGifts,
    setActions,
    setGame: (v) => setValue("game", v as CampaignFormValues["game"]),
    setPrimaryColor: (v) => setValue("primaryColor", v),
    setSecondaryColor: (v) => setValue("secondaryColor", v),
    setLogo: (v) => setValue("logo", v),
    setLogoUri: (v) => setValue("logoUri", v),
    setLabel: (v) => setValue("label", v),
    setStartDate: (v) => setValue("startDate", v),
    setIsLoaded,
    setIsDisabled: (v) => setValue("isDisabled", v),
    setProfile: (v) => setValue("profile", v as CampaignFormValues["profile"]),
    setCampaign,
    saveCampaign,
    registerGetGiftsForSave,
    skipCooldown: formValues.skipCooldown,
    setSkipCooldown: (v) => setValue("skipCooldown", v),
    controlledReviews: formValues.controlledReviews,
    setControlledReviews: (v) => setValue("controlledReviews", v),
    voucherExpiration: formValues.voucherExpiration,
    setVoucherExpiration: (v) => setValue("voucherExpiration", v),
    voucherStart: formValues.voucherStart,
    setVoucherStart: (v) => setValue("voucherStart", v),
    promoCodes: formValues.promoCodes,
    setPromoCodes: (v) => setValue("promoCodes", v),
    refresh: fetchCampaign,
  };

  return (
    <FormProvider {...form}>
      <CampaignContext.Provider value={contextValue}>{children}</CampaignContext.Provider>
    </FormProvider>
  );
};

const useCampaign = () => {
  const context = useContext(CampaignContext);
  if (!context) {
    throw new Error("useCampaign must be used within a CampaignProvider");
  }
  return context;
};

export { CampaignProvider, useCampaign };
